import { KatDropdown } from '@amzn/katal-react';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { StyledDropdown } from 'src/components/general/StyledDropdown';
import { AutoDismissConfirmationDialog } from 'src/components/general/AutoDismissConfirmationDialog';
import { useSelection } from 'src/contexts/SelectionContext';
import { useAssetOperationsInProgress } from 'src/hooks/useAssetOperationsInProgress';
import { useToast } from 'src/contexts/ToastContext';
import styles from './MarketplaceSelector.module.css';
import { MarketPlace } from '@amzn/fire-tv-campaign-automation-service';
import { CMSConfig } from 'src/configs/campaignMetadataConfig';
import { TranslatableConstants } from 'src/constants';

interface MarketplaceSelectorProps {
  className?: string;
}

const MarketplaceSelector = ({ className }: MarketplaceSelectorProps) => {
  const { marketplace, setMarketplace } = useSelection();
  const { addToast } = useToast();
  const isTransforming = useAssetOperationsInProgress();
  const [pendingMarketplace, setPendingMarketplace] = useState<MarketPlace | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [dropdownKey, setDropdownKey] = useState(0);
  const isMarketplaceSwitchingRef = useRef(false);

  // ToDo: The list of applicable marketplaces needs to be passed from CDK
  const marketplaceOptions = CMSConfig?.MARKET_PLACE.map((marketplace) => ({
    name: `${marketplace} Marketplace`,
    value: marketplace,
  }));

  // Show CA disclaimer toast when CA marketplace is selected
  useEffect(() => {
    const timer = setTimeout(() => {
      if (marketplace === MarketPlace.CA && !isMarketplaceSwitchingRef.current) {
        addToast({
          description: TranslatableConstants.CA_MARKETPLACE_DISCLAIMER,
          variant: 'info',
          persistent: false,
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [marketplace, addToast]);

  const handleMarketplaceChange = (event: KatDropdown.ChangeEvent) => {
    const newMarketplace = event.detail.value as MarketPlace;

    if (newMarketplace !== marketplace) {
      setPendingMarketplace(newMarketplace);
      setShowConfirmation(true);
    }
  };

  const handleConfirmMarketplaceChange = useCallback(() => {
    if (pendingMarketplace) {
      isMarketplaceSwitchingRef.current = true;
      setMarketplace(pendingMarketplace);
      // Reload immediately - state updates after reload are irrelevant
      window.location.reload();
    }
  }, [pendingMarketplace, setMarketplace]);

  const handleCancelMarketplaceChange = () => {
    setShowConfirmation(false);
    setPendingMarketplace(null);
    setDropdownKey((prev) => prev + 1);
  };

  return (
    <>
      <StyledDropdown
        key={dropdownKey}
        value={marketplace}
        onChange={handleMarketplaceChange}
        options={marketplaceOptions}
        className={`${styles.container} ${className || ''}`}
        disabled={isTransforming}
      />

      <AutoDismissConfirmationDialog
        isVisible={showConfirmation}
        title="Confirm Marketplace Change"
        message="Changing the marketplace will reset all form data and reload the page. Do you want to continue?"
        confirmText="Continue"
        cancelText="Cancel"
        confirmVariant="primary"
        autoConfirmTimeout={10000}
        onConfirm={handleConfirmMarketplaceChange}
        onCancel={handleCancelMarketplaceChange}
        data-testid="marketplace-confirmation-dialog"
      />
    </>
  );
};

export { MarketplaceSelector };
